import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from 'sonner';
import './App.css';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PublicInvoicePay from './pages/PublicInvoicePay';
import CustomerPortal from './pages/CustomerPortal';
import VehicleLookup from './pages/VehicleLookup';
import Dashboard from './pages/Dashboard';
import Inventory from './pages/Inventory';
import Jobs from './pages/Jobs';
import Customers from './pages/Customers';
import Quotes from './pages/Quotes';
import Equipment from './pages/Equipment';
import GasExpense from './pages/GasExpense';
import Reports from './pages/Reports';
import Team from './pages/Team';
import PhoneSystem from './pages/PhoneSystem';
import DispatchCenter from './pages/DispatchCenter';
import Schedule from './pages/Schedule';
import PricingCalculator from './pages/PricingCalculator';
import AIAssistant from './pages/AIAssistant';
import Documents from './pages/Documents';
import Billing from './pages/Billing';
import Invoices from './pages/Invoices';
import Settings from './pages/Settings';
import Analytics from './pages/Analytics';
import SupportInbox from './pages/SupportInbox';
import MasterTenantManager from './pages/MasterTenantManager';
import SupportButton from './components/SupportButton';
import useTrackPageviews from './hooks/useTrackPageviews';

// Layout Component
import Layout from './components/Layout';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  useTrackPageviews();
  return (
    <>
    <SupportButton />
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
      {/* Public Pay-Now page — accessible by anyone with the token */}
      <Route path="/pay/i/:token" element={<PublicInvoicePay />} />
      {/* Customer Portal — public, no-auth, accessed via customer.portal_token */}
      <Route path="/portal/c/:token" element={<CustomerPortal />} />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="vehicle-lookup" element={<VehicleLookup />} />
        <Route path="dispatch" element={<DispatchCenter />} />
        <Route path="schedule" element={<Schedule />} />
        <Route path="inventory" element={<Inventory />} />
        <Route path="jobs" element={<Jobs />} />
        <Route path="customers" element={<Customers />} />
        <Route path="quotes" element={<Quotes />} />
        <Route path="pricing-calculator" element={<PricingCalculator />} />
        <Route path="phone-system" element={<PhoneSystem />} />
        <Route path="ai-assistant" element={<AIAssistant />} />
        <Route path="documents" element={<Documents />} />
        <Route path="equipment" element={<Equipment />} />
        <Route path="gas-expense" element={<GasExpense />} />
        <Route path="reports" element={<Reports />} />
        <Route path="team" element={<Team />} />
        <Route path="billing" element={<Billing />} />
        <Route path="invoices" element={<Invoices />} />
        <Route path="settings" element={<Settings />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="support-inbox" element={<SupportInbox />} />
        <Route path="tenants" element={<MasterTenantManager />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
